import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import 'katex/dist/katex.min.css'

import './RichMarkdownContent.css'

type RichMarkdownContentProps = {
  contextText: string
  className?: string
}

/**
 * RichMarkdownContent renders markdown content that may contain LaTeX math expressions.
 *
 * Uses react-markdown with remark-math + rehype-katex for rendering.
 * Supports custom LaTeX delimiters:
 * - Inline math: \(...\) - converted internally to $...$ for processing
 * - Display math: \[...\] - converted internally to $$...$$ for processing
 * Also supports GitHub Flavored Markdown (tables, strikethrough, etc.).
 */
const RichMarkdownContent: React.FC<RichMarkdownContentProps> = ({
  contextText,
  className,
}) => {
  // Auto-detect format and convert HTML to markdown if needed
  const markdownContent = useMemo(() => {
    if (!contextText || contextText.trim() === '') {
      return ''
    }

    // First, unescape any escaped markdown syntax (!\[\] → ![])
    // IMPORTANT: Order matters! Unescape compound patterns first, then individual characters
    // PRESERVE LaTeX math delimiters: \(...\) for inline math and \[...\] for display math
    const unescapeMarkdown = (content: string): string => {
      return (
        content
          // Unescape compound patterns first (to avoid double-unescaping)
          .replace(/!\\\[/g, '![') // Unescape image start: !\[ → ![
          // Unescape individual markdown characters
          .replace(/\\\*/g, '*') // Unescape asterisk: \* → *
          .replace(/\\_/g, '_') // Unescape underscore: \_ → _
          .replace(/\\`/g, '`') // Unescape backtick: \` → `
          .replace(/\\#/g, '#') // Unescape hash: \# → #
          // DO NOT unescape \[ and \] - they are LaTeX display math delimiters
          // DO NOT unescape \( and \) - they are LaTeX inline math delimiters
          .replace(/\\-/g, '-') // Unescape dash: \- → -
          .replace(/\\\+/g, '+') // Unescape plus: \+ → +
          .replace(/\\>/g, '>') // Unescape gt: \> → >
          .replace(/\\!/g, '!') // Unescape exclamation: \! → !
          .replace(/\\\|/g, '|') // Unescape pipe: \| → |
      )
    }

    // Normalize markdown lists - remove blank lines between consecutive list items
    // BUT preserve blank lines before the first list item (required by react-markdown)
    const normalizeMarkdownLists = (content: string): string => {
      const lines = content.split('\n')
      const result: string[] = []
      let insideList = false

      lines.forEach((line, index) => {
        const isOrderedListItem = /^\d+\.\s/.test(line)
        const isUnorderedListItem = /^[-*+]\s/.test(line)
        const isListItem = isOrderedListItem || isUnorderedListItem
        const isBlank = line.trim() === ''

        // Check if next line is a list item
        const nextLine = lines[index + 1]
        const nextIsListItem =
          nextLine && (/^\d+\.\s/.test(nextLine) || /^[-*+]\s/.test(nextLine))

        // Entering a list
        if (isListItem && !insideList) {
          insideList = true
          // Ensure blank line before first list item if previous line exists and is not blank
          if (result.length > 0 && result[result.length - 1].trim() !== '') {
            result.push('') // Add blank line before list
          }
          result.push(line)
          return
        }

        // Inside list - add list item
        if (isListItem && insideList) {
          result.push(line)
          return
        }

        // Skip blank lines while inside a list (between items or after last item before exit)
        if (isBlank && insideList) {
          // If next line is NOT a list item, we're exiting the list
          if (!nextIsListItem) {
            insideList = false
            // Skip this blank line that ends the list
            return
          }
          // Skip blank line between list items
          return
        }

        // Exiting list - non-blank, non-list line after list
        if (!isListItem && !isBlank && insideList) {
          insideList = false
        }

        result.push(line)
      })

      return result.join('\n')
    }

    const unescaped = unescapeMarkdown(contextText)

    // Convert LaTeX delimiters from \(...\) and \[...\] to $ and $$ format
    // This allows remark-math to recognize and process the math expressions
    const convertLatexDelimiters = (content: string): string => {
      return (
        content
          // Convert display math: \[...\] → $$...$$
          // Use non-greedy matching to handle multiple expressions
          .replace(/\\\[([\s\S]*?)\\]/g, '$$$$1$$')
          // Convert inline math: \(...\) → $...$
          .replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$')
      )
    }

    const withConvertedDelimiters = convertLatexDelimiters(unescaped)
    // Normalize lists to remove blank lines between items
    return normalizeMarkdownLists(withConvertedDelimiters)
  }, [contextText])

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[
          [
            remarkMath,
            {
              singleDollarTextMath: true, // Enable $ for inline math (converted from \(...\))
            },
          ],
          remarkGfm as any,
        ]}
        rehypePlugins={[rehypeKatex as any, rehypeRaw as any]}
        components={{
          // Custom paragraph styling - keep normal spacing
          p: ({ children }) => {
            return (
              <p
                style={{
                  marginBottom: '8px',
                  marginTop: '8px',
                  fontSize: '14px',
                }}
              >
                {children}
              </p>
            )
          },

          // Handle br tags explicitly
          br: () => <br />,
          // Style ordered lists with explicit numbering
          ol: ({ children }) => (
            <ol
              style={{
                marginBottom: '16px',
                marginTop: '8px',
                marginLeft: '0',
                paddingLeft: '24px',
                listStyleType: 'decimal',
                listStylePosition: 'outside',
              }}
            >
              {children}
            </ol>
          ),
          // Style unordered lists with explicit bullets
          ul: ({ children }) => (
            <ul
              style={{
                marginBottom: '16px',
                marginTop: '8px',
                marginLeft: '0',
                paddingLeft: '24px',
                listStyleType: 'disc',
                listStylePosition: 'outside',
              }}
            >
              {children}
            </ul>
          ),
          // Style list items - remove extra padding
          li: ({ children }) => (
            <li
              style={{
                marginBottom: '2px',
              }}
            >
              {children}
            </li>
          ),
          // Limit image size with wrapper
          img: ({ src, alt }) => (
            <div
              style={{
                maxWidth: '400px',
                maxHeight: '300px',
                display: 'block',
                margin: '12px 0',
                overflow: 'hidden',
              }}
            >
              <img
                src={src}
                alt={alt}
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
          ),
        }}
      >
        {markdownContent}
      </ReactMarkdown>
    </div>
  )
}

export default RichMarkdownContent
